// Centralized application configuration
// Single source of truth for all environment variables

export interface AppConfig {
  env: 'development' | 'production' | 'test';
  logLevel: string;
  apiUrl: string;
  debug: boolean;
  version: string;
  wsUrl: string;
  receiptTransactionType: number;
  withdrawalTransactionType: number;
  paymentTransactionType: number;
  journalTransactionType: number;
  timeZone: string;
  allValue: string;
  receipt: string
  journal: string
  payment: string
  equipment: string;
  fixAmotInterestPolicy: number[];
  flatReateReducingBalanceInterestPolicy: number[];
  fdInterestPolicyIds: number[];
  idleTimeoutMin: number;
  creditUnionName?: string;
  savingCategoryId: number;
  investmentCategoryId: number;
  debtorsCategoryId?: number;
  creditorsCategoryId?: number;
  equipmentCategoryId?: number;
  fdDailyInterestPolicyId: number;
}

// Helper function to safely access environment variables with fallbacks
const getEnvVar = (key: string, fallback: string): string => {
  // Use APP_ prefix as specified by the user
  return process.env[key] || fallback;
};

const getEnvBool = (key: string, fallback: boolean): boolean => {
  const value = getEnvVar(key, String(fallback));
  return value.toLowerCase() === 'true';
};

// Centralized configuration object
export const config: AppConfig = {
  env: getEnvVar('APP_ENV', 'development') as
    | 'development'
    | 'production'
    | 'test',
  logLevel: getEnvVar('REACT_APP_LOG_LEVEL', 'INFO'),
  apiUrl: getEnvVar('REACT_APP_API_URL', 'http://localhost:8000/api'),
  debug: getEnvBool('REACT_APP_ENABLE_DEBUG', false),
  version: getEnvVar('REACT_APP_VERSION', '2.0.0'),
  wsUrl: getEnvVar('REACT_APP_WS_URL', 'ws://localhost:8000/ws'),
  receiptTransactionType: Number(getEnvVar('REACT_APP_RECEIPT_TRANSACTION_TYPE', '1')),
  withdrawalTransactionType: Number(getEnvVar('REACT_APP_WITHDRAWAL_TRANSACTION_TYPE', '2')),
  paymentTransactionType: Number(getEnvVar('REACT_APP_PAYMENT_TRANSACTION_TYPE', '2')),
  journalTransactionType: Number(getEnvVar('REACT_APP_JOURNAL_TRANSACTION_TYPE', '3')),
  receipt: getEnvVar('REACT_APP_RECEIPT', 'RECEIPT'),
  journal: getEnvVar('REACT_APP_JOURNAL', 'JOURNAL'),
  payment: getEnvVar('REACT_APP_PAYMENT', 'PAYMENT'),
  equipment: getEnvVar('REACT_APP_EQUIPMENT', 'EQUIPMENT'),
  timeZone: getEnvVar('REACT_APP_TIME_ZONE', 'Asia/Colombo'),
  allValue: 'AA459016E1825F7CE493BA2F70D166AE',
  fixAmotInterestPolicy: [29], // Example policy IDs for Fix Amortization
  flatReateReducingBalanceInterestPolicy: [25], // Example policy IDs for Flat Rate and Reducing Balance
  fdInterestPolicyIds: [15, 16, 17],
  creditUnionName: getEnvVar('REACT_APP_CREDIT_UNION_NAME', 'Rajida AIB Bank'),
  savingCategoryId: Number(getEnvVar('REACT_APP_SAVING_CATEGORY_ID', '1')),
  investmentCategoryId: Number(getEnvVar('REACT_APP_INVESTMENT_CATEGORY_ID', '4')),
  equipmentCategoryId: Number(getEnvVar('REACT_APP_EQUIPMENT_CATEGORY_ID', '5')),
  debtorsCategoryId: Number(getEnvVar('REACT_APP_DEBTORS_CATEGORY_ID', '6')),
  creditorsCategoryId: Number(getEnvVar('REACT_APP_CREDITORS_CATEGORY_ID', '7')),
  idleTimeoutMin: Number(getEnvVar('REACT_APP_IDLE_TIMEOUT_MIN', '15')),
  fdDailyInterestPolicyId: Number(getEnvVar('REACT_APP_FD_DAILY_INTEREST_POLICY_ID', '17')),
};

// Convenience functions for environment checks
export const isDevelopment = (): boolean => config.env === 'development';
export const isProduction = (): boolean => config.env === 'production';
export const isTest = (): boolean => config.env === 'test';
